import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Card, EmptyState, Spinner } from '@agion/ui'
import { ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Play } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { listChecks, runScan } from './infra_compliance.service'

type CheckStatus = 'pass' | 'fail' | 'warning'

interface ComplianceCheck {
  id: string
  name: string
  category: string
  status: CheckStatus
  lastRun: string
  environment: string
}

function statusBadgeVariant(status: CheckStatus) {
  switch (status) {
    case 'pass':
      return 'success' as const
    case 'fail':
      return 'error' as const
    case 'warning':
      return 'warning' as const
  }
}

function statusLabel(status: CheckStatus) {
  switch (status) {
    case 'pass':
      return 'Pass'
    case 'fail':
      return 'Fail'
    case 'warning':
      return 'Warning'
  }
}

export function CompliancePage() {
  const { t } = useTranslation(['pages'])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [checks, setChecks] = useState<ComplianceCheck[]>([])
  const [isScanning, setIsScanning] = useState(false)

  const loadChecks = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await listChecks()
      const mapped: ComplianceCheck[] = response.data.map(c => ({
        id: c.id,
        name: c.name,
        category: c.framework,
        status: c.status === 'passing' ? 'pass' as const
          : c.status === 'failing' ? 'fail' as const
          : 'warning' as const,
        lastRun: c.lastCheckedAt,
        environment: c.environmentId,
      }))
      setChecks(mapped)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load compliance checks')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadChecks()
  }, [loadChecks])

  async function handleRunScan() {
    try {
      setIsScanning(true)
      await runScan({ environmentId: '' })
      await loadChecks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed')
    } finally {
      setIsScanning(false)
    }
  }

  const passing = checks.filter((c) => c.status === 'pass').length
  const failing = checks.filter((c) => c.status === 'fail').length
  const warnings = checks.filter((c) => c.status === 'warning').length

  const summaryCards = [
    {
      label: t('pages:compliance.summary.total', 'Total Checks'),
      value: checks.length,
      icon: ShieldCheck,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
    },
    {
      label: t('pages:compliance.summary.passing', 'Passing'),
      value: passing,
      icon: CheckCircle2,
      color: 'bg-emerald-500',
      textColor: 'text-emerald-600',
    },
    {
      label: t('pages:compliance.summary.failing', 'Failing'),
      value: failing,
      icon: XCircle,
      color: 'bg-red-500',
      textColor: 'text-red-600',
    },
    {
      label: t('pages:compliance.summary.warnings', 'Warnings'),
      value: warnings,
      icon: AlertTriangle,
      color: 'bg-amber-500',
      textColor: 'text-amber-600',
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={ShieldCheck}
        title={t('pages:compliance.title', 'Compliance')}
        description={t('pages:compliance.description', 'Compliance status and audit checks')}
        actions={
          <Button onClick={handleRunScan} disabled={isScanning}>
            <Play className="h-4 w-4 mr-2" />
            {isScanning
              ? t('pages:compliance.scanning', 'Scanning...')
              : t('pages:compliance.runScan', 'Run Scan')}
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {summaryCards.map((card) => (
              <Card key={card.label}>
                <div className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${card.textColor}`}>{card.value}</p>
                    <p className="text-xs text-slate-500">{card.label}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Checks Table */}
          {checks.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title={t('pages:compliance.empty', 'No compliance checks')}
              description={t('pages:compliance.emptyDesc', 'Run a scan to check compliance status across environments.')}
              action={
                <Button onClick={handleRunScan} disabled={isScanning}>
                  <Play className="h-4 w-4 mr-2" />
                  {t('pages:compliance.runScan', 'Run Scan')}
                </Button>
              }
            />
          ) : (
            <Card>
              <div className="grid grid-cols-5 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <span>{t('pages:compliance.columns.check', 'Check Name')}</span>
                <span>{t('pages:compliance.columns.category', 'Category')}</span>
                <span>{t('pages:compliance.columns.status', 'Status')}</span>
                <span>{t('pages:compliance.columns.lastRun', 'Last Run')}</span>
                <span>{t('pages:compliance.columns.environment', 'Environment')}</span>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {checks.map((check) => (
                  <div
                    key={check.id}
                    className="grid grid-cols-5 gap-4 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <span className="text-slate-900 dark:text-slate-100">{check.name}</span>
                    <span className="text-slate-600 dark:text-slate-400">{check.category}</span>
                    <span>
                      <Badge variant={statusBadgeVariant(check.status)}>
                        {statusLabel(check.status)}
                      </Badge>
                    </span>
                    <span className="text-slate-500 dark:text-slate-400 text-xs">{check.lastRun}</span>
                    <span className="text-slate-600 dark:text-slate-400">{check.environment}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
